/**
 * TextHighlighter component for rendering essay content with highlights.
 * 
 * Features:
 * - Renders markdown content to HTML with syntax highlighting
 * - Highlights text that has inline comments (yellow background)
 * - Shows suggestion marks (strikethrough + insertion)
 * - Handles text selection for creating new comments
 * - Tracks selection position for popover placement
 * 
 * The component converts markdown to HTML, then applies highlight spans
 * based on character offsets from comments and suggestions.
 * 
 * Called by:
 * - EssayDetailPage to render essay content with review features
 */
import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import type { CommentWithReplies, SuggestionWithUser } from '@compendium/shared';

// =============================================================================
// Types
// =============================================================================

/**
 * Selection range in the markdown content.
 */
export interface TextSelection {
  /** Start character offset in markdown */
  start: number;
  /** End character offset in markdown */
  end: number;
  /** The selected text */
  text: string;
  /** Screen position for popover placement */
  rect: DOMRect;
}

interface TextHighlighterProps {
  /** Raw markdown content */
  content: string;
  /** Inline comments to highlight */
  comments: CommentWithReplies[];
  /** Suggestions to display */
  suggestions: SuggestionWithUser[];
  /** Callback when text is selected */
  onTextSelect?: (selection: TextSelection | null) => void;
  /** Callback when clicking a highlighted comment */
  onCommentClick?: (comment: CommentWithReplies) => void;
  /** Callback when clicking a suggestion */
  onSuggestionClick?: (suggestion: SuggestionWithUser) => void;
  /** Optional CSS class name */
  className?: string;
}

// =============================================================================
// Highlight Styles
// =============================================================================

const HIGHLIGHT_STYLES = {
  // Inline comment highlight (yellow)
  comment: {
    backgroundColor: 'rgba(255, 213, 79, 0.3)',
    borderBottom: '2px solid rgba(255, 213, 79, 0.6)',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  // Resolved comment highlight (grey)
  commentResolved: {
    backgroundColor: 'rgba(150, 150, 150, 0.2)',
    borderBottom: '2px solid rgba(150, 150, 150, 0.4)',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  // Suggestion deletion (red strikethrough)
  suggestionDelete: {
    backgroundColor: 'rgba(255, 107, 107, 0.3)',
    textDecoration: 'line-through',
    textDecorationColor: 'rgba(255, 107, 107, 0.8)',
    cursor: 'pointer',
  },
  // Suggestion insertion (green underline)
  suggestionInsert: {
    backgroundColor: 'rgba(107, 255, 107, 0.3)',
    borderBottom: '2px solid rgba(107, 255, 107, 0.6)',
    cursor: 'pointer',
  },
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Represents a range in the text that needs highlighting.
 */
interface HighlightRange {
  start: number;
  end: number;
  type: 'comment' | 'comment-resolved' | 'suggestion-delete' | 'suggestion-insert';
  id: number;
  data?: CommentWithReplies | SuggestionWithUser;
}

/**
 * Build a list of highlight ranges from comments and suggestions.
 * Ranges are sorted by start position.
 */
function buildHighlightRanges(
  comments: CommentWithReplies[],
  suggestions: SuggestionWithUser[]
): HighlightRange[] {
  const ranges: HighlightRange[] = [];

  // Add comment ranges
  for (const comment of comments) {
    if (
      comment.type === 'inline' &&
      comment.selectionStart !== null &&
      comment.selectionEnd !== null
    ) {
      ranges.push({
        start: comment.selectionStart,
        end: comment.selectionEnd,
        type: comment.resolved ? 'comment-resolved' : 'comment',
        id: comment.id,
        data: comment,
      });
    }
  }

  // Add suggestion ranges (only pending suggestions)
  for (const suggestion of suggestions) {
    if (suggestion.status === 'pending') {
      // Add deletion range (original text)
      ranges.push({
        start: suggestion.selectionStart,
        end: suggestion.selectionEnd,
        type: 'suggestion-delete',
        id: suggestion.id,
        data: suggestion,
      });
    }
  }

  // Sort by start position
  ranges.sort((a, b) => a.start - b.start);

  return ranges;
}

/**
 * Apply highlights to text content.
 * Returns an array of React elements with highlighted spans.
 */
function applyHighlights(
  text: string,
  ranges: HighlightRange[],
  onCommentClick?: (comment: CommentWithReplies) => void,
  onSuggestionClick?: (suggestion: SuggestionWithUser) => void
): React.ReactNode[] {
  if (ranges.length === 0) {
    return [text];
  }

  const result: React.ReactNode[] = [];
  let lastEnd = 0;

  for (const range of ranges) {
    // Skip if range is out of bounds
    if (range.start >= text.length) continue;
    
    // Add text before this range
    if (range.start > lastEnd) {
      result.push(text.substring(lastEnd, range.start));
    }

    // Determine style and click handler
    let style: React.CSSProperties;
    let onClick: (() => void) | undefined;

    switch (range.type) {
      case 'comment':
        style = HIGHLIGHT_STYLES.comment;
        onClick = range.data && onCommentClick
          ? () => onCommentClick(range.data as CommentWithReplies)
          : undefined;
        break;
      case 'comment-resolved':
        style = HIGHLIGHT_STYLES.commentResolved;
        onClick = range.data && onCommentClick
          ? () => onCommentClick(range.data as CommentWithReplies)
          : undefined;
        break;
      case 'suggestion-delete':
        style = HIGHLIGHT_STYLES.suggestionDelete;
        onClick = range.data && onSuggestionClick
          ? () => onSuggestionClick(range.data as SuggestionWithUser)
          : undefined;
        break;
      case 'suggestion-insert':
        style = HIGHLIGHT_STYLES.suggestionInsert;
        onClick = range.data && onSuggestionClick
          ? () => onSuggestionClick(range.data as SuggestionWithUser)
          : undefined;
        break;
      default:
        style = {};
    }

    // Add highlighted span
    const end = Math.min(range.end, text.length);
    const highlightedText = text.substring(range.start, end);
    
    result.push(
      <span
        key={`${range.type}-${range.id}-${range.start}`}
        style={style}
        onClick={onClick}
        data-highlight-type={range.type}
        data-highlight-id={range.id}
      >
        {highlightedText}
      </span>
    );

    // For suggestions, also show the suggested text inline
    if (range.type === 'suggestion-delete' && range.data) {
      const suggestion = range.data as SuggestionWithUser;
      result.push(
        <span
          key={`suggestion-insert-${range.id}`}
          style={HIGHLIGHT_STYLES.suggestionInsert}
          onClick={onClick}
          data-highlight-type="suggestion-insert"
          data-highlight-id={range.id}
        >
          {suggestion.suggestedText}
        </span>
      );
    }

    lastEnd = end;
  }

  // Add remaining text after last range
  if (lastEnd < text.length) {
    result.push(text.substring(lastEnd));
  }

  return result;
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * TextHighlighter component.
 * Renders markdown with comment and suggestion highlights.
 */
function TextHighlighter({
  content,
  comments,
  suggestions,
  onTextSelect,
  onCommentClick,
  onSuggestionClick,
  className = '',
}: TextHighlighterProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectionTimeout, setSelectionTimeout] = useState<NodeJS.Timeout | null>(null);

  // Build highlight ranges
  const highlightRanges = useMemo(
    () => buildHighlightRanges(comments, suggestions),
    [comments, suggestions]
  );

  // Handle text selection
  const handleMouseUp = useCallback(() => {
    // Clear any existing timeout
    if (selectionTimeout) {
      clearTimeout(selectionTimeout);
    }

    // Set a new timeout to check selection after 300ms
    const timeout = setTimeout(() => {
      const selection = window.getSelection();
      
      if (!selection || selection.isCollapsed || !containerRef.current) {
        onTextSelect?.(null);
        return;
      }

      const selectedText = selection.toString().trim();
      if (!selectedText) {
        onTextSelect?.(null);
        return;
      }

      // Get the selection range
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      // Calculate character offsets in the markdown content
      // This is a simplified approach - in production you'd need more sophisticated mapping
      const start = content.indexOf(selectedText);
      if (start === -1) {
        // Try to find a partial match
        onTextSelect?.(null);
        return;
      }

      onTextSelect?.({
        start,
        end: start + selectedText.length,
        text: selectedText,
        rect,
      });
    }, 300);

    setSelectionTimeout(timeout);
  }, [content, onTextSelect, selectionTimeout]);

  // Clear selection when clicking elsewhere
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't clear if clicking on a highlight
    const target = e.target as HTMLElement;
    if (target.hasAttribute('data-highlight-type')) {
      return;
    }

    // Clear selection timeout
    if (selectionTimeout) {
      clearTimeout(selectionTimeout);
      setSelectionTimeout(null);
    }
  }, [selectionTimeout]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (selectionTimeout) {
        clearTimeout(selectionTimeout);
      }
    };
  }, [selectionTimeout]);

  // Custom renderer for text nodes to apply highlights
  // Note: This is a simplified approach. For production, you'd want to
  // properly map markdown positions to rendered HTML positions.
  const renderedContent = useMemo(() => {
    if (highlightRanges.length === 0) {
      return content;
    }

    // Apply highlights to the raw content
    return applyHighlights(content, highlightRanges, onCommentClick, onSuggestionClick);
  }, [content, highlightRanges, onCommentClick, onSuggestionClick]);

  // If we have highlights, render with custom processing
  // Otherwise, use standard markdown rendering
  if (highlightRanges.length > 0) {
    return (
      <div
        ref={containerRef}
        className={`markdown-content text-highlighter ${className}`}
        onMouseUp={handleMouseUp}
        onMouseDown={handleMouseDown}
        style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
      >
        {/* Render with highlights - simplified approach */}
        <div style={{ fontFamily: 'var(--font-serif)', fontSize: '18px', color: '#e0e0e0' }}>
          {renderedContent}
        </div>
      </div>
    );
  }

  // Standard markdown rendering when no highlights
  return (
    <div
      ref={containerRef}
      className={`markdown-content text-highlighter ${className}`}
      onMouseUp={handleMouseUp}
      onMouseDown={handleMouseDown}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeHighlight]}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default TextHighlighter;
